import clsx from 'clsx'
import type { ReactNode } from 'react'

import { Pressable } from '@/src/elements/pressable'
import { ICON } from '@/src/general/enums/icons'
import { dataFocusVisibleClasses } from '@/src/styles/classes'

import { Icon } from '../../media/Icon'
import { buttonCommonVariants } from '../variants/button-variants-common'
import type { TDefaultButtonProps } from './Button.type'
import { Button as NextUiButton } from './nextui-replacement/Button'
import { defaultButtonCommonVariants, defaultButtonTextCommonVariants } from './variants/default-button-variants-common'
import defaultButtonWebVariants from './variants/default-button-variants-web'

export const Button = ({
  ref,
  testID,
  colors,
  size,
  variant,
  contrast,
  shadow,
  isDisabled,
  isIconOnly,
  isInactive,
  startIcon,
  endIcon,
  onPress,
  className,
  textClassName,
  isLoading,
  ...otherProps
}: TDefaultButtonProps): ReactNode => (
  <NextUiButton
    ref={ref}
    data-testid={testID}
    as={Pressable}
    tabIndex={0}
    className={clsx(
      buttonCommonVariants({ isDisabled: isDisabled || isInactive, size, isIconOnly, isLoading }),
      dataFocusVisibleClasses,
      defaultButtonCommonVariants({ colors, variant, shadow, contrast, size }),
      defaultButtonTextCommonVariants({ colors, variant, shadow, contrast, size }),
      defaultButtonWebVariants({ colors, variant, shadow }),
      textClassName,
      className,
    )}
    isLoading={isLoading}
    spinner={
      <Icon
        testID={`${testID}-spinner`}
        name={ICON.LOADER}
        size={size === 'xs' ? 'sm' : 'md'}
        className={clsx(
          defaultButtonTextCommonVariants({ colors, variant, contrast }),
          'animate-spin border-none',
          textClassName,
        )}
      />
    }
    variant="solid"
    size="lg"
    radius="full"
    startContent={
      startIcon &&
      !isLoading && (
        <Icon
          style={{ pointerEvents: 'none' }}
          name={startIcon}
          size={size === 'xs' ? 'sm' : 'md'}
          className={clsx(defaultButtonTextCommonVariants({ colors, variant, contrast }), 'border-none', textClassName)}
        />
      )
    }
    endContent={
      !isLoading &&
      !isIconOnly &&
      endIcon && (
        <Icon
          style={{ pointerEvents: 'none' }}
          name={endIcon}
          size={size === 'xs' ? 'sm' : 'md'}
          className={clsx(defaultButtonTextCommonVariants({ colors, variant, contrast }), 'border-none', textClassName)}
        />
      )
    }
    isIconOnly={isIconOnly}
    isDisabled={isDisabled}
    disableRipple={isDisabled}
    onClick={onPress}
    onKeyDown={(e) => {
      if (e.key === 'Enter') {
        // @ts-expect-error - onKeyDown is not of the same type as onPress
        onPress?.(e)
      }
    }}
    style={{ textWrap: 'wrap' }}
    {...otherProps}
  />
)
